// Port of ecommerce/chart/items_list.coffee — the per-product metrics table that sits under
// the chart. Audited against the ItemsListView base it used to extend: of that base's
// behaviour it used only `onLabelClick`, which the block shim supplies as a callback (it
// reaches into the filters sidebar, which is page-level). No skips-to and no comments.
//
// It opens in the order the report holds -- the one the chart is drawn in -- and every column
// sorts from there. A sort the researcher picks names an order back, which the block settles on
// and the shell writes down, so the chart follows the table and the report remembers it.

import { pluralize } from '@aytm/app/stats/lib/util/helpers'
import { processText } from '@aytm/app/stats/page/components/lib/utils'
import { byCellValue, byPosition } from '@aytm/app/stats/page/question-views/oe/lib/table-utils'
import { DEFAULT_SORT_ORDER } from '@aytm/app/stats/page/question-views/ec/lib/report-params'
import { buildTraitTable } from '@aytm/app/stats/page/question-views/sh/lib/answer-trait-table'
import { formatWeeks } from '@aytm/app/stats/page/question-views/ec/lib/formatWeeks'
import { getTraitValues } from '@aytm/app/stats/page/question-views/ec/components/chart/lib/traitValues'
import {
  isTraitExcluded,
  SUBSCRIBE_RATE_MODES,
  viewModeCategoryOf
} from '@aytm/app/stats/page/question-views/ec/components/chart/lib/viewOptions'

// Palette length: answer-variant colour indices cycle modulo this size.
const PALETTE_SIZE = 21

// Presence in this map decides the column shape: a listed mode renders a plain value
// column, an unlisted one falls back to a percentage progress bar.
const RESPONSES_TITLES = {
  attention: 'Time',
  time: 'Time',
  timeToFirstClick: 'Time',
  pVolume: 'Quantity',
  pAmount: 'Amount',
  subscribeRate: 'Rate',
  subscribeSpend: 'Amount',
  considerationRate: 'Rate',
  churnRate: 'Rate',
  reversalRate: 'Rate',
  bannerImpressions: 'Impressions',
  bannerClicks: 'Clicks',
  bannerCtr: 'CTR',
  bannerFunnelPopup: 'Conversion',
  bannerFunnelCart: 'Conversion',
  bannerAvgViewToClick: 'Time'
}

// A few modes want a more explicit heading than the shared RESPONSES_TITLES label.
const COLUMN_TITLES = {
  subscribeRate: 'Subscribe rate',
  considerationRate: 'Consideration rate',
  churnRate: 'Churn rate',
  reversalRate: 'Reversal rate',
  bannerCtr: 'CTR',
  bannerFunnelPopup: 'Click-to-popup',
  bannerFunnelCart: 'Click-to-cart'
}

const SECOND_MODES = ['attention', 'time', 'timeToFirstClick', 'bannerAvgViewToClick']
const PERCENT_SUFFIX_MODES = ['bannerCtr', 'bannerFunnelPopup', 'bannerFunnelCart']
const MONEY_MODES = ['pAmount', 'subscribeSpend']
const TWO_DECIMAL_MODES = ['pAmount', 'pVolume', 'subscribeSpend']
const INTEGER_MODES = ['bannerImpressions', 'bannerClicks']

const isRate = viewMode => PERCENT_SUFFIX_MODES.includes(viewMode) || SUBSCRIBE_RATE_MODES.includes(viewMode)

const pct = percentDecimals => value => `${value.toFixed(percentDecimals ?? 1)}%`

const formatValue = (viewMode, priceTemplate, percentDecimals) => value => {
  if (isRate(viewMode)) return pct(percentDecimals)(value)

  let out
  if (TWO_DECIMAL_MODES.includes(viewMode)) out = value.toFixed(2)
  else if (INTEGER_MODES.includes(viewMode)) out = Math.round(value).toString()
  else out = value.toFixed(1)

  if (MONEY_MODES.includes(viewMode) && priceTemplate) out = priceTemplate.replace(/{{price}}/gi, out)
  if (SECOND_MODES.includes(viewMode)) out += ` ${pluralize(value, 'second', 'seconds')}`

  return out
}

// Banner and subscribe metrics live outside answer.aN, so reading a cell value dispatches
// on the same category the chart uses.
const cellAmount = ({ answer, a, key, viewMode }) => {
  switch (viewModeCategoryOf(viewMode)) {
    case 'banner': {
      const banner = answer?.banners?.[key] || {}
      const funnel = answer?.banners?.funnel?.[key] || {}
      switch (viewMode) {
        case 'bannerImpressions':
          return banner.impressions || 0
        case 'bannerClicks':
          return banner.clicks || 0
        case 'bannerCtr':
          return (banner.ctr || 0) * 100
        case 'bannerFunnelPopup':
          return (funnel.clickToPopupRate || 0) * 100
        case 'bannerFunnelCart':
          return (funnel.clickToCartRate || 0) * 100
        case 'bannerAvgViewToClick':
          return banner.avgViewToClick || 0
        default:
          return 0
      }
    }
    case 'subscribe': {
      const sub = answer?.subscribe?.[key] || {}
      // `frequency` never reaches here — it is handled by its own branch above.
      return 'subscribeSpend' === viewMode ? sub.subscribeSpend || 0 : (sub[viewMode] || 0) * 100
    }
    default:
      return a[viewMode] || 0
  }
}

const buildCols = ({ viewMode, percentDecimals, priceTemplate, freqTop2, freqDist }) => {
  const cols = [{ type: 'title', title: 'Answers' }]

  if ('totalPickedUp' === viewMode) {
    cols.push(
      // the two shares that make up the total; the sort grammar can only name the products' own
      // order or the last column, so a click here would silently sort by the total instead
      { type: 'value', title: 'Purchased', format: pct(percentDecimals), sortable: false },
      { type: 'value', title: 'Picked up only', format: pct(percentDecimals), sortable: false },
      // only the share is known here, so the column names itself where the % marker would sit and
      // heads no count slot
      { type: 'progress', title: '', percentLabel: 'Total products picked up', percentDecimals, width: 200 }
    )
    return cols
  }

  if ('frequency' === viewMode) {
    freqTop2.forEach(cat => {
      cols.push({ type: 'value', title: `Every ${formatWeeks(cat.weeks, true)}`, format: pct(percentDecimals) })
    })
    if (freqDist.length > 0) cols.push({ type: 'progress', title: 'Total subscriptions', percentDecimals })
    return cols
  }

  if (RESPONSES_TITLES[viewMode]) {
    cols.push({
      type: 'value',
      title: COLUMN_TITLES[viewMode] || RESPONSES_TITLES[viewMode],
      format: formatValue(viewMode, priceTemplate, percentDecimals)
    })
    return cols
  }

  // Purchase selection: the respondents who bought each product and the share they make.
  cols.push({ type: 'progress', title: 'Respondents', percentDecimals })
  return cols
}

// The products keep their own order under an equal value, so a tie never shuffles the list.
const withSorts = cols =>
  cols.map(({ sortable, ...col }, index) => {
    if (false === sortable) return col
    if ('title' === col.type) return { ...col, sort: byPosition }

    return { ...col, sort: byCellValue(index, 'progress' === col.type ? 'percents' : 'value') }
  })

// The column the stored order names: the products by their own order, or else the metric the
// chart is sorted by -- the last column, which is the value or the total.
const applySortedColumn = (cols, sortOrder) => {
  const order = String(sortOrder || DEFAULT_SORT_ORDER)

  const index = 'i' === order[0] ? 0 : cols.length - 1
  const direction = order.endsWith('asc') ? 1 : -1

  return cols.map((col, i) => (i === index ? { ...col, sorted: direction } : col))
}

export const calcChartTableProps = ({
  id,
  answer,
  answerVariants = [],
  viewMode,
  sortOrder,
  percentDecimals,
  priceTemplate,
  trait,
  traitName,
  isTraitType,
  ignoreTraits,
  clickable,
  onLabelClick,
  onSort
}) => {
  const isLoading = 'loading' === answer
  const values = isLoading || answer == undefined ? {} : answer

  const freqDist = values?.subscribe?.frequencyDistribution || []
  const freqTop2 = freqDist.slice(0, 2)
  const traitValues = getTraitValues(trait)

  const cols = buildCols({ viewMode, percentDecimals, priceTemplate, freqTop2, freqDist })

  const rows = answerVariants.map(av => {
    const key = `a${av.position}`
    const a = values[key] || {}

    const cells = [
      {
        title: processText(av.text),
        label: `A${av.position}`,
        colorIndex: trait && isTraitType ? 0 : ((av.position - 1) % PALETTE_SIZE) + 1
      }
    ]

    if ('totalPickedUp' === viewMode) {
      cells.push(
        { value: a.percents || 0, loading: isLoading },
        { value: a.pickedUpPercents || 0, loading: isLoading },
        { percents: (a.percents || 0) + (a.pickedUpPercents || 0), loading: isLoading }
      )
    } else if ('frequency' === viewMode) {
      const productFreq = values?.subscribe?.[key]?.frequency || []
      freqTop2.forEach(cat => {
        const entry = productFreq.find(f => f.weeks === cat.weeks)
        cells.push({ value: entry?.pct || 0, loading: isLoading })
      })
      if (freqDist.length > 0) cells.push({ percents: productFreq[0]?.pct || 0, loading: isLoading })
    } else {
      const amount = cellAmount({ answer: values, a, key, viewMode })
      if (RESPONSES_TITLES[viewMode]) {
        cells.push({ value: amount, loading: isLoading })
      } else {
        cells.push({ amount, percents: 'amount' === viewMode ? a.percents || 0 : 0, loading: isLoading })
      }
    }

    if (Object.hasOwn(av, 'image')) cells[0].image = av.image
    if (clickable) cells[0].onLabelClick = onLabelClick

    const row = { cells, position: av.position }

    if (trait && isTraitType && !isTraitExcluded(viewMode) && true !== ignoreTraits) {
      const traitAnswer = a[traitName]
      if (traitAnswer !== undefined) {
        row.table = buildTraitTable({
          id,
          trait,
          traitName,
          traitValues,
          traitAnswer,
          viewMode,
          priceTemplate,
          responsesTitle: RESPONSES_TITLES[viewMode],
          percentDecimals,
          productTotalPickedUp: a.totalPickedUp
        })
      }
    }

    return row
  })

  return { cols: applySortedColumn(withSorts(cols), sortOrder), rows, onSort }
}
